import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import { CopyTrainingDocument, ICopyTrainingMutation } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { ConfirmDialog } from 'common/components/ConfirmDialog';

type TCopiedTraining = NonNullable<
    ICopyTrainingMutation['copyTraining']
>['training'];

interface IChildrenParams {
    loading: boolean;
    copyTraining(): void;
}

interface IProps {
    id: string;
    onSuccess?(newTraining: TCopiedTraining): void;
    children(params: IChildrenParams): React.ReactNode;
}

export const TrainingCopyAction = ({ id, onSuccess, children }: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();

    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const [copyTraining, { loading: loadingCopy }] =
        useMutation(CopyTrainingDocument);

    const handleCopyTraining = async () => {
        let newTraining: TCopiedTraining | undefined;

        try {
            const response = await copyTraining({
                variables: { id },
                update: (cache) => {
                    // Remove training lists from cache so they will be refetched
                    cache.evict({ fieldName: 'overviewTrainings' });
                    cache.evict({ fieldName: 'paginatedTrainings' });
                    cache.gc();
                },
            });

            newTraining = response.data?.copyTraining?.training;
        } catch (error) {
            showApolloError(error);
            setShowCopyDialog(false);

            return;
        }

        if (!newTraining) return;

        displaySnackbar(translate('trainingActionSuccess.copy'), {
            variant: 'success',
        });

        setShowCopyDialog(false);
        onSuccess?.(newTraining);
    };

    return (
        <>
            {children({
                copyTraining: () => {
                    !loadingCopy && setShowCopyDialog(true);
                },
                loading: loadingCopy,
            })}

            <ConfirmDialog
                confirmText={translate('copy')}
                loading={loadingCopy}
                open={showCopyDialog}
                title={translate('copyTrainingMessage.title')}
                onCancel={() => {
                    setShowCopyDialog(false);
                }}
                onClose={() => setShowCopyDialog(false)}
                onConfirm={handleCopyTraining}
            >
                {translate('copyTrainingMessage.text')}
            </ConfirmDialog>
        </>
    );
};
